import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Users, UserRound, Settings, ChevronDown, Link as LinkIcon, HelpCircle, Wrench } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Check,
  Ban,
  Zap,
  Receipt,
  Star,
  Smartphone,
  ShieldCheck,
  Clock,
  ArrowRight,
  BellRing,
  LayoutDashboard,
  MessageSquare,
} from "lucide-react";
import dashboardMobile1 from "@/assets/dashboard-mobile-1.jpg";
import dashboardMobile2 from "@/assets/dashboard-mobile-2.jpg";
import reminderSmsMobile from "@/assets/reminder-sms-mobile.jpg";
import reminderEmailMobile from "@/assets/reminder-email-mobile.jpg";
import receiptPdfMobile1 from "@/assets/receipt-pdf-mobile-1.jpg";
import receiptPdfMobile2 from "@/assets/receipt-pdf-mobile-2.jpg";
import reviewSmsMobile from "@/assets/review-sms-mobile.jpg";
import reviewEmailMobile from "@/assets/review-email-mobile.jpg";
import jordanPhoto from "@/assets/jordan.jpg.asset.json";

const steps = [
  {
    n: "01",
    title: "Finish the job",
    body: "Tradesperson, stylist, cleaner, mobile mechanic — whatever you do, just do your thing.",
    icon: Check,
  },
  {
    n: "02",
    title: "Enter a few details",
    body: "Pop in the customer's name, contact and how much to charge. Takes under 30 seconds.",
    icon: Receipt,
  },
  {
    n: "03",
    title: "Send your bank details",
    body: "Hound fires off a text or email with everything they need — account name, sort code and account number.",
    icon: MessageSquare,
  },
  {
    n: "04",
    title: "Customer pays direct",
    body: "Money lands straight in your bank. No card reader, no middleman, no fees nibbling away.",
    icon: Zap,
  },
  {
    n: "05",
    title: "PDF receipt, automatic",
    body: "Once marked as paid, your customer gets a professional branded PDF receipt — no extra work from you.",
    icon: ShieldCheck,
  },
  {
    n: "06",
    title: "Collect a 5-star review",
    body: "A review link goes out with the receipt so happy customers can shout about you on Google, Facebook and more.",
    icon: Star,
  },
  {
    n: "07",
    title: "Auto-chase non-payers",
    body: "Anyone dragging their feet gets polite SMS and email reminders on a schedule — until they pay up.",
    icon: BellRing,
  },
];

export default function Landing() {
  const { user, signOut } = useAuth();
  const [profilePicture, setProfilePicture] = useState<string>("");

  useEffect(() => {
    if (!user?.id) { setProfilePicture(""); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("profiles").select("avatar_url").eq("id", user.id).maybeSingle();
      if (!cancelled) setProfilePicture(data?.avatar_url || user.user_metadata?.avatar_url || "");
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    document.title = "Hound — F**K the card reader. Get paid direct to bank.";
    const meta =
      document.querySelector('meta[name="description"]') ||
      (() => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        document.head.appendChild(m);
        return m;
      })();
    meta.setAttribute(
      "content",
      "Send your bank details by SMS or email and get paid direct — no card reader, no fees. Automatic reminders for unpaid invoices and a live paid/unpaid dashboard. £19/mo."
    );
    const link =
      document.querySelector('link[rel="canonical"]') ||
      (() => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        document.head.appendChild(l);
        return l;
      })();
    link.setAttribute("href", "https://houndpayments.co.uk/");
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur border-b border-border">
        <div className="container flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 font-bold text-xl">
            Hound
          </Link>
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Avatar className="h-10 w-10">
                  <AvatarImage src={profilePicture} alt={user.email || "User"} />
                  <AvatarFallback className="bg-mint text-mint-foreground">
                    {user.email ? user.email.substring(0, 2).toUpperCase() : "U"}
                  </AvatarFallback>
                </Avatar>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full focus:ring-0">
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-xl">
                    <DropdownMenuItem asChild><Link to="/dashboard"><LayoutDashboard className="mr-2 h-4 w-4" /><span>Dashboard</span></Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/customers"><Users className="mr-2 h-4 w-4" /><span>Customers</span></Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/account/profile"><UserRound className="mr-2 h-4 w-4" /><span>Account Profile</span></Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/account/payment-settings"><Settings className="mr-2 h-4 w-4" /><span>Payment Settings</span></Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/account/review-links"><LinkIcon className="mr-2 h-4 w-4" /><span>Review Links</span></Link></DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => signOut()}>Sign out</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/help"><Button variant="ghost" size="sm" className="gap-1.5"><HelpCircle className="h-4 w-4" />Help</Button></Link>
                <Link to="/auth"><Button variant="ghost" size="sm">Sign in</Button></Link>
                <Link to="/signup"><Button variant="mint" size="sm">Sign up</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mint-soft opacity-40 pointer-events-none" />
        <div className="container relative py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: text */}
            <div className="text-center md:text-left">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mint/30 text-mint-foreground text-xs font-semibold uppercase tracking-wider mb-6">
                <Ban className="w-3 h-3" /> Card reader free · 0% fees
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight mb-6 break-words">
                F**K the
                <br />
                <span className="text-mint-dark">card reader.</span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-xl mx-auto md:mx-0 mb-8">
                Send your bank details by SMS or email. Customer pays direct into your bank.
                Hound chases the late ones for you. You keep every penny.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start mb-4">
                <Link to="/signup">
                  <Button variant="mint" size="default" className="w-44">
                    Sign up <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="#how">
                  <Button variant="outline" size="default" className="w-44">
                    See how it works
                  </Button>
                </a>
              </div>
              <p className="text-sm text-muted-foreground flex items-center justify-center md:justify-start gap-2 mb-5">
                <Clock className="w-4 h-4" /> Cancel anytime
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                <a
                  href="#"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background hover:border-mint transition-colors"
                  aria-label="Download on the App Store"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left leading-none">
                    <div className="text-[10px] text-muted-foreground">Download on the</div>
                    <div className="text-sm font-semibold -mt-0.5">App Store</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Right: pricing card */}
            <div className="flex justify-center">
              <Card className="w-full max-w-sm md:max-w-xs lg:max-w-sm p-6 md:p-4 lg:p-6 border-2 border-mint shadow-xl bg-background">
                <div className="text-center mb-4">
                  <span className="inline-block px-3 py-1 rounded-full bg-mint text-mint-foreground text-xs font-bold uppercase tracking-wider mb-3">
                    Hound Pro
                  </span>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl md:text-4xl lg:text-5xl font-extrabold">£19</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Cancel anytime
                  </p>
                </div>
                <ul className="space-y-2 mb-6 text-sm md:text-xs lg:text-sm">
                  {[
                    "Unlimited customers & bank details",
                    "50 SMS usage per month",
                    "Unlimited payment requests (Email)",
                    "Automatic payment reminders",
                    "Paid / unpaid dashboard",
                    "Branded PDF invoices & receipts",
                    "Automatic review requests",
                    "iPhone & Android apps",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-mint-dark shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  No card reader to buy. No transaction fees. Ever.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How it works — horizontal process */}
      <section id="how" className="bg-mint-50 py-16 md:py-24 overflow-hidden">
        <div className="container">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-mint/30 text-mint-dark text-xs font-bold uppercase tracking-wider mb-4">
              How it works
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              From "all done" to paid
            </h2>
            <p className="text-muted-foreground leading-relaxed text-sm">
              Hound handles the awkward bits — sending bank details, chasing late payers and collecting reviews — so you can crack on with the next job.
            </p>
          </div>

          {/* Timeline: vertical on mobile, horizontal on md+ */}
          <div className="relative max-w-2xl md:max-w-7xl mx-auto">
            {/* Connecting line */}
            <div
              className="absolute left-4 top-4 bottom-4 w-0.5 bg-gradient-to-b from-mint via-mint-dark to-mint md:left-[7.142857%] md:right-[7.142857%] md:top-4 md:bottom-auto md:h-0.5 md:w-auto md:bg-gradient-to-r"
              aria-hidden="true"
            />

            <ul className="space-y-5 md:space-y-0 md:grid md:grid-cols-7 md:items-start md:gap-4 lg:gap-8">
              {steps.map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.n} className="relative flex items-center gap-3 md:flex-col md:items-center md:gap-3 md:min-w-0">
                    {/* Step node */}
                    <div className="relative z-10 shrink-0 w-8 h-8 rounded-full bg-gradient-mint text-mint-foreground flex items-center justify-center shadow-md ring-4 ring-mint-50 md:mx-auto">
                      <Icon className="w-3.5 h-3.5" />
                    </div>

                    {/* Pill card */}
                    <Card className="flex-1 px-3 py-2 border-2 border-transparent hover:border-mint transition-colors shadow-sm rounded-full flex items-center gap-3 md:flex-none md:justify-center md:text-center md:px-2 md:py-1.5 md:gap-1.5 md:rounded-2xl lg:rounded-full">
                      <h3 className="text-sm font-semibold md:text-[10px] md:leading-snug lg:text-xs lg:whitespace-nowrap">{s.title}</h3>
                    </Card>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="text-center mt-14">
            <Link to="/signup">
              <Button variant="mint" size="lg" className="text-base">
                Sign up <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison section */}
      <section className="container py-16 md:py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              Tired of feeding the <span className="line-through text-destructive">card machine</span>?
            </h2>
            <p className="max-w-xl mx-auto text-muted-foreground text-lg">
              Card readers charge you per tap. Hound gets you paid directly to your bank account. No hardware, no fees, no fuss.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center relative">
            {/* VS Badge */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[calc(50%+2.5rem)] md:-translate-y-1/2 z-30 flex items-center justify-center w-[4.5rem] h-[4.5rem] md:w-12 md:h-12 bg-background rounded-full border-2 border-foreground shadow-lg text-foreground font-bold italic text-base md:text-sm">
              VS
            </div>

            {/* Traditional Readers Card */}
            <div className="bg-card rounded-3xl overflow-hidden border border-foreground shadow-sm opacity-80 md:scale-95 md:origin-right transition-all">
              <div className="bg-foreground h-20 flex items-center justify-center">
                <h3 className="text-background font-bold uppercase tracking-widest text-base">Traditional Card Readers</h3>
              </div>
              <div className="p-8 space-y-6">
                {[
                  "1.5%–2.75% on every single transaction",
                  "Hardware that costs £29–£199 up front",
                  "Funds locked up for 1–3 working days",
                  "No way to chase customers who never pay",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-4">
                    <div className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-destructive/10 flex items-center justify-center">
                      <span className="text-destructive font-bold text-sm leading-none">✕</span>
                    </div>
                    <p className="text-foreground font-medium">{t}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* The Hound Way Card */}
            <div className="relative bg-card rounded-3xl overflow-hidden border-2 border-mint shadow-2xl md:scale-105 z-20">
              <div className="absolute top-3 right-3 z-10">
                <span className="bg-white/20 text-mint-foreground text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded backdrop-blur-sm border border-white/30">
                  Recommended
                </span>
              </div>
              <div className="bg-gradient-mint h-24 flex items-center justify-center">
                <h3 className="text-mint-foreground font-bold text-2xl uppercase tracking-widest">The Hound Way</h3>
              </div>
              <div className="p-8 space-y-6">
                {[
                  "0% transaction fees — you keep 100%",
                  "No card reader to buy",
                  "Automatic reminders for unpaid invoices",
                  "Live paid / unpaid dashboard",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-4">
                    <div className="mt-1 flex-shrink-0 w-7 h-7 rounded-full bg-mint/30 flex items-center justify-center">
                      <Check className="w-5 h-5 text-mint-dark" strokeWidth={3} />
                    </div>
                    <p className="text-foreground font-semibold text-lg">{t}</p>
                  </div>
                ))}
                <div className="pt-2">
                  <Link to="/signup">
                    <Button variant="mint" className="w-full py-6 text-base font-semibold rounded-xl">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>


      <section id="dashboard" className="container py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-mint/30 text-mint-dark text-xs font-bold uppercase tracking-wider mb-4">
              In your pocket
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Track paid and unpaid invoices.
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Open the app and instantly see what you've been paid, what's still
              outstanding, and who needs a nudge. No spreadsheets. No guesswork.
            </p>
            <ul className="space-y-3">
              {[
                "Paid vs unpaid totals for any date range",
                "One-tap payment reminders by SMS or email",
                "Customer list with live status badges",
                "Mark as paid, edit, or chase from one screen",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-mint-dark mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-center gap-4 md:gap-6">
            <img
              src={dashboardMobile1}
              alt="Hound mobile dashboard showing paid and unpaid totals"
              width={1024}
              height={1024}
              loading="lazy"
              className="w-1/2 max-w-[260px] rounded-2xl shadow-2xl rotate-[-4deg]"
            />
            <img
              src={dashboardMobile2}
              alt="Hound customer list with payment status and reminder buttons"
              width={1024}
              height={1024}
              loading="lazy"
              className="w-1/2 max-w-[260px] rounded-2xl shadow-2xl rotate-[4deg] mt-8"
            />
          </div>
        </div>
      </section>

      {/* Chase unpaid invoices */}
      <section id="chase" className="bg-mint-50 py-16 md:py-24 overflow-hidden">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center gap-4 md:gap-6 order-2 md:order-1">
              <img
                src={reminderSmsMobile}
                alt="Hound automatic SMS payment reminder on mobile"
                width={1024}
                height={1024}
                loading="lazy"
                className="w-1/2 max-w-[260px] rounded-2xl shadow-2xl rotate-[-4deg]"
              />
              <img
                src={reminderEmailMobile}
                alt="Hound automatic email payment reminder on mobile"
                width={1024}
                height={1024}
                loading="lazy"
                className="w-1/2 max-w-[260px] rounded-2xl shadow-2xl rotate-[4deg] mt-8"
              />
            </div>
            <div className="order-1 md:order-2">
              <span className="inline-block px-3 py-1 rounded-full bg-mint/30 text-mint-dark text-xs font-bold uppercase tracking-wider mb-4">
                Automatic chasing
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Automatic text and email reminders for unpaid invoices.
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                No more awkward "just following up" texts. Hound sends polite, firm
                reminders by SMS and email on a schedule that actually works —
                so you can focus on the next job, not the last invoice.
              </p>
              <ul className="space-y-3">
                {[
                  "Friendly SMS reminders with your bank details",
                  "Professional email follow-ups with invoice attached",
                  "Custom reminder schedule — daily, weekly, or monthly",
                  "Stop chasing. Start building.",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-mint-dark mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="receipts" className="container py-16 md:py-24 overflow-hidden">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-mint/30 text-mint-dark text-xs font-bold uppercase tracking-wider mb-4">
              Professional every time
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Auto generated PDF invoices for every customer.
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Every SMS and email includes a professional PDF invoice or receipt
              with your business logo, details, and a clear "PAID" or "UNPAID"
              stamp. Your customers know exactly what they're paying for — and
              you look like the pro you are.
            </p>
            <ul className="space-y-3">
              {[
                "Auto-generated PDF attached to every message",
                "Your logo and business details on every invoice",
                "Clear PAID or UNPAID stamp for instant clarity",
                "Download, print, or share in one tap",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-mint-dark mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-center gap-4 md:gap-6">
            <img
              src={receiptPdfMobile1}
              alt="Hound branded PDF invoice with PAID stamp on mobile"
              width={1024}
              height={1024}
              loading="lazy"
              className="w-1/2 max-w-[260px] rounded-2xl shadow-2xl rotate-[-4deg]"
            />
            <img
              src={receiptPdfMobile2}
              alt="Hound email with PDF receipt attachment on mobile"
              width={1024}
              height={1024}
              loading="lazy"
              className="w-1/2 max-w-[260px] rounded-2xl shadow-2xl rotate-[4deg] mt-8"
            />
          </div>
        </div>
      </section>

      {/* Review links */}
      <section id="reviews" className="bg-mint-50 py-16 md:py-24 overflow-hidden">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center gap-4 md:gap-6 order-2 md:order-1">
              <img
                src={reviewSmsMobile}
                alt="Hound SMS payment confirmation with Google review link"
                width={1024}
                height={1024}
                loading="lazy"
                className="w-1/2 max-w-[260px] rounded-2xl shadow-2xl rotate-[-4deg]"
              />
              <img
                src={reviewEmailMobile}
                alt="Hound email with review buttons for Google Facebook and Checkatrade"
                width={1024}
                height={1024}
                loading="lazy"
                className="w-1/2 max-w-[260px] rounded-2xl shadow-2xl rotate-[4deg] mt-8"
              />
            </div>
            <div className="order-1 md:order-2">
              <span className="inline-block px-3 py-1 rounded-full bg-mint/30 text-mint-dark text-xs font-bold uppercase tracking-wider mb-4">
                More 5-star reviews
              </span>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Automatic review links on every platform you care about.
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Right after payment, every SMS and email includes a review link
                to whatever platform you choose — Google, Facebook, Checkatrade,
                Trustpilot, or anywhere else. One happy customer, one more
                glowing review. It builds itself.
              </p>
              <ul className="space-y-3">
                {[
                  "Your review link in every payment SMS and email",
                  "Works with Google, Facebook, Checkatrade, Trustpilot",
                  "Set it once — it goes out automatically forever",
                  "Turn happy jobs into real reputation growth",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-mint-dark mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="bg-mint-50 py-16 md:py-24">
        <div className="container max-w-5xl">
          <h2 className="text-3xl md:text-5xl font-bold mb-10 text-center">FAQ</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                q: "So there's no card payment at all?",
                a: "Correct. Hound sends your bank details (account name, sort code, account number) by SMS or email. The customer pays you direct from their banking app. No card, no terminal, no fees.",
              },
              {
                q: "What if the customer doesn't pay?",
                a: "Hound automatically sends them friendly reminders by SMS or email on a schedule you control — so you never have to chase awkwardly yourself.",
              },
              {
                q: "How do I track who's paid and who hasn't?",
                a: "The dashboard shows live paid vs unpaid totals plus a full customer list with status badges. Mark anything as paid with one tap.",
              },
              {
                q: "Can I cancel anytime?",
                a: "Yes. There's no contract. Cancel from inside the app with one tap.",
              },
            ].map(({ q, a }) => (
              <Card key={q} className="p-6">
                <h3 className="font-semibold mb-2">{q}</h3>
                <p className="text-muted-foreground">{a}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-mint py-20">
        <div className="container text-center text-mint-foreground">
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6">
            Bin the card reader. Keep the cash.
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Get paid direct, chase the late ones automatically, and see it all
            on one dashboard.
          </p>
          <Link to="/signup">
            <Button size="lg" variant="secondary" className="text-base">
              Sign up <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* About */}
      <section className="bg-mint-50 py-16 md:py-24">
        <div className="container max-w-4xl">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-mint/30 text-mint-dark text-xs font-bold uppercase tracking-wider mb-4">
              Meet the founder
            </span>
            <h2 className="text-3xl md:text-5xl font-bold">Built by a tradesperson, for tradespeople</h2>
          </div>
          <Card className="p-8 md:p-12 text-center md:text-left">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="shrink-0">
                <img
                  src={jordanPhoto.url}
                  alt="Jordan Drovandi, founder of Hound Payments"
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover shadow-lg ring-4 ring-mint/30"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">Jordan Drovandi</h3>
                <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider mb-4 flex items-center justify-center md:justify-start gap-2">
                  <Wrench className="w-4 h-4" /> UK Tradesman & Founder
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  After working as a UK-based tradesman for 20 years, I was fed up "hounding" people for payment — and sick of card reader fees eating into my earnings.
                  So I built Hound Payments — to get paid faster, track revenue, and just stay organised.
                  No more awkward chasing. No more lost invoices. No more terminal fees. Just a simple tool that understands how real tradespeople work.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© {new Date().getFullYear()} Hound Payments. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/auth" className="hover:text-foreground">Sign in</Link>
            <a href="mailto:hello@houndpayments.co.uk" className="hover:text-foreground">Contact</a>
            <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
